import makeStyles from '@mui/styles/makeStyles';
import {Check} from "lucide-react";
import classNames from "classnames";
import {map, sortBy} from "lodash";
import React, {useMemo} from "react";
import {color, font, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import SelectField from "components/redesign/SelectField";

const useStyles = makeStyles(() => ({
  helperText: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    marginTop: 6,
  },
  // Quiz variant: the options are the field, rather than hiding behind a menu.
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    ...type.body,
    fontWeight: font.MEDIUM,
    color: color.TEXT,
    background: color.SURFACE,
    border: `1px solid ${color.BORDER}`,
    borderRadius: 12,
    padding: "15px 16px",
    cursor: "pointer",
    textAlign: "left",
    transition: "background 0.15s ease, border-color 0.15s ease",
    "&:hover": {
      borderColor: color.TEXT_MUTED,
    },
  },
  rowSelected: {
    background: color.ACCENT_TINT,
    borderColor: color.ACCENT,
  },
  box: {
    width: 20,
    height: 20,
    flexShrink: 0,
    borderRadius: 6,
    border: `1px solid ${color.BORDER}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "transparent",
  },
  boxSelected: {
    background: color.ACCENT,
    borderColor: color.ACCENT,
    color: color.ON_ACCENT,
  },
}));

const MultiSelectDropdown = ({
  DropDownItems,
  onAnswered,
  value = [],
  maxSelections,
  minSelections,
  variant,
}) => {
  const {st} = useSetTranslation();
  const classes = useStyles();
  const orderedOptionItems = sortBy(DropDownItems, (option) => option.displayOrder);

  const optionsByKey = useMemo(
    () => new Map(orderedOptionItems.map((option) => [option.key, option])),
    [orderedOptionItems]
  );

  // Normalize the value array so that it always contains the latest option
  // objects from `orderedOptionItems`, matched by `key`. This avoids issues
  // when language changes or options are re-fetched, which would otherwise
  // change object identities and break selection toggling.
  const normalizedValue = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) {
      return [];
    }

    return value
      .map((selected) =>
        selected && selected.key && optionsByKey.has(selected.key)
          ? optionsByKey.get(selected.key)
          : null
      )
      .filter((item) => item !== null);
  }, [value, optionsByKey]);

  const getHelperText = () => {
    if (!minSelections && !maxSelections) {
      return null;
    }

    const parts = [];
    if (minSelections) parts.push(`minimum ${minSelections}`);
    if (maxSelections) parts.push(`maximum ${maxSelections}`);

    return `${normalizedValue.length} of ${parts.join(', ')} option(s) selected`;
  };

  const helperText = getHelperText();

  if (variant === "rows") {
    const isSelected = (option) => normalizedValue.some((item) => item.key === option.key);
    const toggle = (option) => {
      const next = isSelected(option)
        ? normalizedValue.filter((item) => item.key !== option.key)
        : [...normalizedValue, option];
      if (maxSelections && next.length > maxSelections) return;
      onAnswered(next);
    };

    return (
      <div>
        <div className={classes.rows}>
          {map(orderedOptionItems, (option) => {
            const selected = isSelected(option);
            return (
              <button
                type={"button"}
                key={option.key}
                aria-pressed={selected}
                className={classNames(classes.row, selected && classes.rowSelected)}
                onClick={() => toggle(option)}
              >
                <span className={classNames(classes.box, selected && classes.boxSelected)}>
                  <Check size={14} strokeWidth={3}/>
                </span>
                {st(option.label, option.localeLabel)}
              </button>
            );
          })}
        </div>
        {helperText && <div className={classes.helperText}>{helperText}</div>}
      </div>
    );
  }

  const options = map(orderedOptionItems, (option) => ({
    key: option.key,
    label: st(option.label, option.localeLabel),
    helpText: option.helpText,
    disabled: option.isDisabled,
  }));

  // The app stores answers as the option objects, so keys go out to the field
  // and come back as options.
  const handleChange = (keys) =>
    onAnswered(map(keys, (key) => optionsByKey.get(key)).filter(Boolean));

  return (
    <div>
      <SelectField
        multiple
        options={options}
        value={map(normalizedValue, (item) => item.key)}
        onChange={handleChange}
        maxSelections={maxSelections}
      />
      {helperText && <div className={classes.helperText}>{helperText}</div>}
    </div>
  );
};

export default MultiSelectDropdown;
